import type { BattlepassRewardCell } from '@/hooks/useBattlepass';

function getLevelReward(
  map: Record<string | number, BattlepassRewardCell> | undefined,
  level: number,
): BattlepassRewardCell | undefined {
  if (!map) return undefined;
  return map[level] ?? map[String(level)];
}

function mergeRewardCell(
  visual: BattlepassRewardCell,
  progress: BattlepassRewardCell | undefined,
  progressReady: boolean,
): BattlepassRewardCell {
  if (!progressReady || !progress) {
    return { ...visual, state: '', claimed: 0, user_reward_id: 0 };
  }

  return {
    ...visual,
    ...progress,
    image_url: progress.image_url || visual.image_url,
    SN_Name: progress.SN_Name || visual.SN_Name,
    Quantity: progress.Quantity ?? visual.Quantity,
    has_multiple: progress.has_multiple ?? visual.has_multiple,
    group_reward_ids: progress.group_reward_ids ?? visual.group_reward_ids,
    group_items: progress.group_items?.length ? progress.group_items : visual.group_items,
  };
}

export function mergeBattlepassRewards(
  visual: Record<string | number, BattlepassRewardCell> | undefined,
  progress: Record<string | number, BattlepassRewardCell> | undefined,
  maxLevels: number,
  progressReady: boolean,
): Record<number, BattlepassRewardCell> {
  const result: Record<number, BattlepassRewardCell> = {};
  for (let level = 1; level <= maxLevels; level += 1) {
    const visualCell = getLevelReward(visual, level);
    const progressCell = getLevelReward(progress, level);
    if (visualCell) {
      result[level] = mergeRewardCell(visualCell, progressCell, progressReady);
    } else if (progressCell) {
      result[level] = progressCell;
    }
  }
  return result;
}

export function preloadBattlepassImages(
  battlepass: {
    banner_img: string | null;
    reward_img: string | null;
    banner_rev?: number | null;
    updated_at?: string | null;
    id?: number;
  } | null | undefined,
  freeRewards: Record<string | number, BattlepassRewardCell>,
  premiumRewards: Record<string | number, BattlepassRewardCell>,
  resolveUrl: (path: string | null | undefined, cacheToken?: string | number) => string,
): void {
  const urls = new Set<string>();
  const bannerToken =
    battlepass?.banner_rev ?? battlepass?.updated_at ?? `${battlepass?.id ?? ''}-${battlepass?.banner_img ?? ''}`;
  if (battlepass?.banner_img) urls.add(resolveUrl(battlepass.banner_img, bannerToken));
  if (battlepass?.reward_img) urls.add(resolveUrl(battlepass.reward_img));
  for (const reward of [...Object.values(freeRewards), ...Object.values(premiumRewards)]) {
    if (reward?.image_url) urls.add(resolveUrl(reward.image_url));
    for (const item of reward.group_items ?? []) {
      if (item.image_url) urls.add(resolveUrl(item.image_url));
    }
  }
  urls.forEach((url) => {
    if (!url) return;
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  });
}
